<template>
  <div data-test="post-sync-removal-panel" class="card">
    <div class="card-header">{{ label }}</div>
    <div class="card-body">
      <div class="card-text">
        <p>
          <small>{{ trans("feature.postSyncRemovalDescription") }}</small>
        </p>
        <p v-if="!canManage">
          <small data-test="post-sync-removal-super-only">{{
            trans("feature.postSyncRemovalSuperOnly")
          }}</small>
        </p>

        <div
          v-for="product in activeProducts"
          :key="product.id"
          class="mb-2"
          :data-test="`post-sync-removal-toggle-${product.id}`"
        >
          <DerToggleWithLabel
            :model-value="isEnabled(product.id)"
            :disabled="!canManage"
            :label="product.display_name"
            @update:model-value="toggle(product.id, $event)"
          />
          <small
            v-if="enabledSince(product.id)"
            :data-test="`post-sync-removal-enabled-since-${product.id}`"
          >
            {{ trans("feature.postSyncRemovalEnabledSince") }}
            {{ enabledSince(product.id) }}
          </small>
        </div>

        <p v-if="activeProducts.length === 0">
          <small>{{ trans("feature.postSyncRemovalNoProducts") }}</small>
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import DerToggleWithLabel from "../../design-system/DerToggleWithLabel.vue";

const TRANSFER_TYPE = "picsara";

// Mirrors PostSyncRemovalConfig::REMOVAL_CAPABLE_PRODUCT_IDS — only the
// Photo/Capture products can be opted in; the server rejects anything else.
const REMOVAL_CAPABLE_PRODUCT_IDS = [3, 115, 130];

export default {
  components: { DerToggleWithLabel },
  props: {
    features: {},
    featureId: {},
    region: {},
    label: {
      type: String,
      default: "",
    },
  },
  computed: {
    canManage() {
      return this.$can("manage_super_settings");
    },
    activeProducts() {
      return Object.values(this.region?.all_products ?? {}).filter(
        (product) =>
          product.active && REMOVAL_CAPABLE_PRODUCT_IDS.includes(product.id),
      );
    },
    enabledProducts() {
      return (
        this.features[this.featureId].config?.[TRANSFER_TYPE]?.products ?? {}
      );
    },
  },
  methods: {
    isEnabled(productId) {
      return productId in this.enabledProducts;
    },
    enabledSince(productId) {
      const enabledAt = this.enabledProducts[productId]?.enabled_at;
      return enabledAt ? enabledAt.substring(0, 10) : null;
    },
    toggle(productId, on) {
      if (!this.canManage) {
        return;
      }

      this.$vueAlert
        .confirm(
          this.trans(
            on
              ? "feature.postSyncRemovalConfirm"
              : "feature.postSyncRemovalDisableConfirm",
          ),
          undefined,
          undefined,
          {
            confirmButtonText: this.trans("general.modalConfirmButton"),
            cancelButtonText: this.trans("general.modalCancelButton"),
          },
        )
        .then(() => this.apply(productId, on))
        .catch(() => {});
    },
    apply(productId, on) {
      const config = JSON.parse(
        JSON.stringify(this.features[this.featureId].config ?? {}),
      );
      config[TRANSFER_TYPE] = config[TRANSFER_TYPE] ?? {};
      config[TRANSFER_TYPE].products = config[TRANSFER_TYPE].products ?? {};

      if (on) {
        // The eligibility marker (enabled_at/enabled_by) is stamped server-side.
        config[TRANSFER_TYPE].products[productId] = {};
      } else {
        delete config[TRANSFER_TYPE].products[productId];
      }

      this.features[this.featureId].config = config;
      this.features[this.featureId].active =
        Object.keys(config[TRANSFER_TYPE].products).length > 0;
    },
  },
};
</script>
